import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Camera, ChevronRight, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
// @ts-ignore - JSX module
import { SignatureField } from '@/components/ui/app/signature-field';
// @ts-ignore - JSX module
import { PhotoThumbnail, usePhotoLightbox, getGuestPhotoUrl } from '@/components/ui/PhotoGallery';
// @ts-ignore - JSX module
import SheetPhotoCapture from '@/pages/folders/index/_partials/sheets/SheetPhotoCapture';
// @ts-ignore - JSX module
import VideoCaptureSheet from '@/pages/procedures/reports/_partials/form/VideoCaptureSheet';
// @ts-ignore - JSX module
import { VideoFieldCreate } from '@/pages/procedures/reports/_partials/form/VideoField';
// @ts-ignore - JSX module
import AlertDialogExitConfirmation from '@/pages/_partials/AlertDialogExitConfirmation';
import { useGuestAuth } from '@/lib/contexts/guest-auth-context';
// @ts-ignore - JSX module
import { useGeolocation } from '@/lib/contexts/device-permissions-context';
// @ts-ignore - JS module
import { useExitConfirmation } from '@/lib/hooks/use-exit-confirmation';
import { backendToFrontendFieldType, frontendToBackendFieldType, isVideoFieldType } from '@/lib/utils/procedure-field-types';
import GuestReportReadOnly from './GuestReportReadOnly';
import GuestVideoDialog from './GuestVideoDialog';
import { horodatyApi } from '@/lib/utils/horodaty-api';

/**
 * Un champ média (photo, photo certifiée ou vidéo certifiée) stocke des uniq_id de documents
 * dans `uploadedPhotos` plutôt que dans `formData`.
 */
function isMediaFieldType(type: string): boolean {
    const frontendType = backendToFrontendFieldType(type);
    return frontendType === 'photo' || frontendType === 'video';
}

/**
 * Nombre minimum de médias requis pour un champ photo/vidéo
 */
function getMinMediaCount(field: any): number {
    const frontendType = backendToFrontendFieldType(field.type);
    const explicitMin = frontendType === 'video' ? field.minVideos : field.minPhotos;
    return explicitMin || field.options?.min || 1;
}

/**
 * Construit l'entrée média affichée par PhotoThumbnail à partir d'un document du rapport.
 * Les URLs passent par les routes de l'invitation : un invité n'est pas autorisé sur /api/pictures.
 */
function documentToMedia(doc: any, invitationUuid: string) {
    const isVideo = typeof doc.mime === 'string' && doc.mime.startsWith('video/');
    return {
        uniq_id: doc.uniq_id,
        previewUrl: getGuestPhotoUrl(invitationUuid, doc.uniq_id, 'thumbnail'),
        thumbnailUrl: getGuestPhotoUrl(invitationUuid, doc.uniq_id, 'thumbnail'),
        originalUrl: getGuestPhotoUrl(invitationUuid, doc.uniq_id, 'original'),
        mediaType: isVideo ? 'video' : 'photo',
        mime: doc.mime,
    };
}

/**
 * Composant pour afficher un champ photo avec lightbox intégrée
 */
function PhotoFieldRenderer({ 
    field, 
    uploadedPhotos, 
    onPhotoCapture, 
    isUploadingPhoto 
}: {
    field: any;
    uploadedPhotos: any[];
    onPhotoCapture: () => void;
    isUploadingPhoto: boolean;
}) {
    const { t } = useTranslation('public');
    const { openLightbox } = usePhotoLightbox(uploadedPhotos);
    const photos = uploadedPhotos || [];
    const minPhotos = field.minPhotos || field.options?.min || 1;
    const maxPhotos = field.maxPhotos || field.options?.max || 6;
    const canAddMore = photos.length < maxPhotos;

    return (
        <div>
            <div className="flex items-center justify-between mb-[12px]">
                <div className="flex-1">
                    <div className="flex items-center gap-[8px] mb-[4px]">
                        <h3 className="text-[15px] text-foreground">
                            {field.label}
                            {field.required && <span className="text-destructive ml-[4px]">*</span>}
                        </h3>
                        <span className="bg-secondary text-secondary-foreground rounded-[6px] px-[8px] py-[2px] text-[11px] font-medium">
                            {t('reportForm.tokenPerPhoto')}
                        </span>
                    </div>
                    <p className="text-[12px] text-muted-foreground">
                        {t('reportForm.minPhoto', { count: minPhotos })}
                    </p>
                </div>
                <span className="text-[13px] text-muted-foreground">
                    {photos.length}/{maxPhotos}
                </span>
            </div>

            {/* Photo Grid with PhotoSwipe */}
            {photos.length > 0 && (
                <div className="flex flex-wrap gap-[12px] mb-[16px]">
                    {photos.map((photo: any, photoIndex: number) => (
                        <PhotoThumbnail
                            key={photo.uniq_id || photoIndex}
                            photo={photo}
                            index={photoIndex}
                            showRemoveButton={false}
                            showHorodateBadge={true}
                            onClick={photo.uniq_id ? openLightbox : undefined}
                            width={200}
                            height={200}
                        />
                    ))}
                </div>
            )}

            {/* Add Photo Button */}
            {canAddMore && (
                <div>
                    <Button
                        type="button"
                        variant="plain"
                        onClick={onPhotoCapture}
                        disabled={isUploadingPhoto}
                        className="w-full h-auto border-2 border-dashed border-secondary bg-secondary/5 rounded-[12px] py-[20px] hover:bg-secondary/10 transition-colors disabled:opacity-50 flex flex-col items-center"
                    >
                        {isUploadingPhoto ? (
                            <Loader2 className="h-6 w-6 text-secondary mb-[8px] animate-spin" />
                        ) : (
                            <Camera className="h-6 w-6 text-secondary mb-[8px]" />
                        )}
                        <p className="text-[14px] text-secondary">
                            {isUploadingPhoto ? t('reportForm.uploadingEllipsis') : t('reportForm.takeTimestampedPhoto')}
                        </p>
                    </Button>
                    <p className="text-[11px] text-secondary text-center mt-[8px] font-medium">
                        {t('reportForm.photosTimestampedInstantly')}
                    </p>
                </div>
            )}
        </div>
    );
}

/**
 * Composant de rendu d'un champ de formulaire
 */
function FieldRenderer({ 
    field, 
    value, 
    onChange, 
    onPhotoCapture, 
    onVideoClick,
    uploadedPhotos = [], 
    isUploadingPhoto = false 
}: {
    field: any;
    value: any;
    onChange: (value: any) => void;
    onPhotoCapture: () => void;
    onVideoClick: (video: any) => void;
    uploadedPhotos?: any[];
    isUploadingPhoto?: boolean;
}) {
    const { t } = useTranslation('public');
    const frontendType = backendToFrontendFieldType(field.type);

    switch (frontendType) {
        case 'dropdown':
            return (
                <div>
                    <label className="text-[13px] text-foreground mb-[8px] block">
                        {field.label}
                        {field.required && <span className="text-destructive ml-[4px]">*</span>}
                    </label>
                    <select
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full bg-surface border border-border focus:border-2 focus:border-brand rounded-[12px] px-[16px] py-[12px] text-[14px] text-foreground outline-none transition-colors appearance-none cursor-pointer"
                    >
                        <option value="">{t('reportForm.selectPlaceholder')}</option>
                        {(field.options || []).map((option: string) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>
            );

        case 'text':
            return (
                <div>
                    <label className="text-[13px] text-foreground mb-[8px] block">
                        {field.label}
                        {field.required && <span className="text-destructive ml-[4px]">*</span>}
                    </label>
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.placeholder || ''}
                        className="w-full bg-surface border border-border focus:border-2 focus:border-brand rounded-[12px] px-[16px] py-[12px] text-[14px] text-foreground outline-none transition-colors"
                    />
                </div>
            );

        case 'date':
            return (
                <div>
                    <label className="text-[13px] text-foreground mb-[8px] block">
                        {field.label}
                        {field.required && <span className="text-destructive ml-[4px]">*</span>}
                    </label>
                    <input
                        type="date"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full bg-surface border border-border focus:border-2 focus:border-brand rounded-[12px] px-[16px] py-[12px] text-[14px] text-foreground outline-none transition-colors"
                    />
                </div>
            );

        case 'comment':
            return (
                <div>
                    <label className="text-[13px] text-foreground mb-[8px] block">
                        {field.label}
                        {field.required && <span className="text-destructive ml-[4px]">*</span>}
                    </label>
                    <textarea
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.placeholder || ''}
                        rows={4}
                        className="w-full bg-surface border border-border focus:border-2 focus:border-brand rounded-[12px] px-[16px] py-[12px] text-[14px] text-foreground outline-none transition-colors resize-none"
                    />
                </div>
            );

        case 'checkbox': {
            const selectedOptions = Array.isArray(value) ? value : [];
            return (
                <Field>
                    <FieldLabel>
                        {field.label}
                        {field.required && <span className="text-destructive ml-[4px]">*</span>}
                    </FieldLabel>
                    <div className="space-y-[12px]">
                        {(field.options || []).map((option: string) => (
                            <label key={option} className="flex items-center gap-[12px] cursor-pointer">
                                <Checkbox
                                    checked={selectedOptions.includes(option)}
                                    onCheckedChange={(checked) => {
                                        if (checked) {
                                            onChange([...selectedOptions, option]);
                                        } else {
                                            onChange(selectedOptions.filter((o: string) => o !== option));
                                        }
                                    }}
                                />
                                <span className="text-[14px] text-foreground">
                                    {option}
                                </span>
                            </label>
                        ))}
                    </div>
                </Field>
            );
        }

        case 'signature':
            return (
                <div>
                    <label className="text-[13px] text-foreground mb-[8px] block">
                        {field.label}
                        {field.required && <span className="text-destructive ml-[4px]">*</span>}
                    </label>
                    <SignatureField
                        value={value}
                        onChange={onChange}
                        disabled={false}
                    />
                </div>
            );

        case 'photo':
            return (
                <PhotoFieldRenderer
                    field={field}
                    uploadedPhotos={uploadedPhotos}
                    onPhotoCapture={onPhotoCapture}
                    isUploadingPhoto={isUploadingPhoto}
                />
            );

        case 'video':
            return (
                <VideoFieldCreate
                    field={field}
                    videos={uploadedPhotos || []}
                    onVideoCapture={onPhotoCapture}
                    onVideoClick={onVideoClick}
                    isUploading={isUploadingPhoto}
                />
            );

        default:
            return (
                <div className="text-muted-foreground text-sm">
                    {t('reportForm.unsupportedFieldType', { type: field.type })}
                </div>
            );
    }
}

/**
 * Page de remplissage de rapport pour les invités
 */
export default function InviteReportPage() {
    const { t } = useTranslation('public');
    const navigate = useNavigate();
    const { invitationContext, invitationUuid, isAuthenticated, loadInvitationContext, markInvitationCompleted } = useGuestAuth();
    // Position fournie par le DevicePermissionsProvider du PublicLayout : les sheets de capture
    // ne renvoient pas de coordonnées, et l'API exige latitude/longitude pour horodater un média.
    const { position } = useGeolocation();

    // États du formulaire
    const [template, setTemplate] = useState<any>(null);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);
    const [draftData, setDraftData] = useState<any>(null);
    const [interventionData, setInterventionData] = useState<any>(null);
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [uploadedPhotos, setUploadedPhotos] = useState<Record<string, any[]>>({});
    const [showPhotoCapture, setShowPhotoCapture] = useState(false);
    const [captureFieldId, setCaptureFieldId] = useState<string | null>(null);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState<string | null>(null);
    const [playingVideo, setPlayingVideo] = useState<any | null>(null);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contextLoadAttempted, setContextLoadAttempted] = useState(false);

    // Restaurer le contexte d'invitation si on est authentifié mais pas le contexte
    useEffect(() => {
        if (isAuthenticated && !invitationContext && !contextLoadAttempted) {
            setContextLoadAttempted(true);
            loadInvitationContext();
        }
    }, [isAuthenticated, invitationContext, loadInvitationContext, contextLoadAttempted]);

    // Charger le brouillon existant et pré-remplir les données
    useEffect(() => {
        console.log('[Report] loadDraftData useEffect - invitationUuid:', invitationUuid, 'isAuthenticated:', isAuthenticated);
        if (!invitationUuid || !isAuthenticated) return;

        const loadDraftData = async () => {
            setIsLoadingTemplate(true);
            setError(null);

            try {
                // Charger les données du brouillon via l'API guest
                // L'invitation_uuid est dans l'URL, l'API charge l'intervention depuis l'invitation
                console.log('[Report] loadDraftData - calling getGuestIntervention');
                const draftResponse = await horodatyApi.getGuestIntervention(invitationUuid);
                const draftIntervention = draftResponse.data?.data || draftResponse.data;

                // Stocker les données brutes pour la vue read-only
                setInterventionData(draftIntervention);
                
                const procedureReport = draftIntervention.procedureReport;
                const templateVersion = procedureReport?.procedureTemplateVersion;
                const configuration = templateVersion?.configuration || procedureReport?.configuration;
                const existingAnswers = procedureReport?.answers;

                console.log('[Report] loadDraftData - procedureReport:', !!procedureReport, 'templateVersion:', !!templateVersion, 'configuration:', !!configuration, 'existingAnswers:', !!existingAnswers);
                console.log('[Report] loadDraftData - configuration sections count:', configuration?.sections?.length);
                
                setTemplate({
                    name: templateVersion?.name || invitationContext?.procedure_template.name,
                    configuration: configuration,
                    uniq_id: templateVersion?.uniq_id,
                });

                // Plus besoin de stocker folderId/interventionId, l'API guest utilise l'invitation_uuid
                setDraftData({
                    procedureTemplateVersion: templateVersion,
                });

                // Pré-remplir le formulaire avec les réponses existantes
                if (configuration?.sections) {
                    const existingFormData: Record<string, any> = {};
                    
                    configuration.sections.forEach((section: any) => {
                        const answerSection = existingAnswers?.sections?.find((as: any) => as.id === section.id);
                        
                        section.fields?.forEach((field: any) => {
                            const fieldKey = `${section.id}-${field.id}`;
                            const answerField = answerSection?.fields?.find((af: any) => af.id === field.id);
                            existingFormData[fieldKey] = answerField?.value ?? field.default ?? '';
                        });
                    });
                    
                    setFormData(existingFormData);
                }

                // Charger les photos existantes depuis les documents
                if (draftIntervention.documents && existingAnswers?.sections) {
                    const existingPhotos: Record<string, any[]> = {};
                    
                    existingAnswers.sections.forEach((section: any) => {
                        section.fields?.forEach((field: any) => {
                            if (field.value && Array.isArray(field.value)) {
                                const fieldKey = `${section.id}-${field.id}`;
                                const photos = field.value
                                    .map((docId: string) => {
                                        const doc = draftIntervention.documents.find((d: any) => d.uniq_id === docId);
                                        return doc ? documentToMedia(doc, invitationUuid) : null;
                                    })
                                    .filter(Boolean);

                                if (photos.length > 0) {
                                    existingPhotos[fieldKey] = photos;
                                }
                            }
                        });
                    });
                    
                    setUploadedPhotos(existingPhotos);
                }
            } catch (err: any) {
                console.error('Error loading draft:', err);
                setError(err.response?.data?.message || t('public:impossibleDeChargerLeRapport'));
            } finally {
                setIsLoadingTemplate(false);
            }
        };

        loadDraftData();
    }, [invitationUuid, isAuthenticated, invitationContext]);

    // Sections du formulaire
    const sections = useMemo(() => {
        return template?.configuration?.sections || [];
    }, [template?.configuration]);

    const currentSection = sections[currentSectionIndex];
    const totalSections = sections.length;
    const progress = ((currentSectionIndex + 1) / totalSections) * 100;
    const isFirst = currentSectionIndex === 0;
    const isLast = currentSectionIndex === totalSections - 1;

    // Gérer le changement de valeur d'un champ
    const handleFieldChange = useCallback((sectionId: string | number, fieldId: string | number, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [`${sectionId}-${fieldId}`]: value,
        }));
    }, []);

    // Fonction pour sauvegarder le brouillon avant de quitter
    const saveDraftBeforeExit = useCallback(async () => {
        console.log('[Report] saveDraftBeforeExit called, status:', invitationContext?.status);
        // Ne pas sauvegarder si le rapport est déjà finalisé
        if (invitationContext?.status === 'completed') {
            console.log('[Report] SKIP save - report is completed');
            return;
        }
        
        if (!invitationUuid || !template?.configuration?.sections) {
            return;
        }

        try {
            // Construire les answers manuellement avec les bonnes clés (sectionId-fieldId)
            const answers = {
                sections: template.configuration.sections.map((section: any) => ({
                    id: section.id,
                    fields: (section.fields || []).map((field: any) => {
                        const fieldKey = `${section.id}-${field.id}`;

                        // Pour les champs média (photo / vidéo), utiliser les IDs des documents uploadés
                        if (isMediaFieldType(field.type)) {
                            const photos = uploadedPhotos[fieldKey] || [];
                            return {
                                id: field.id,
                                type: frontendToBackendFieldType(field.type),
                                value: photos.map((p: any) => p.uniq_id),
                            };
                        }
                        
                        // Pour les autres champs, utiliser formData
                        return {
                            id: field.id,
                            type: frontendToBackendFieldType(field.type),
                            value: formData[fieldKey] ?? null,
                        };
                    }),
                })),
            };

            await horodatyApi.updateGuestReport(invitationUuid, { answers });

            console.log('[Report] saveDraftBeforeExit - draft saved OK, answers:', JSON.stringify(answers).substring(0, 500));
            toast.success(t('reportForm.draftSaved'));
        } catch (err) {
            console.error('Erreur sauvegarde brouillon:', err);
            // On ne bloque pas la sortie en cas d'erreur
        }
    }, [invitationUuid, formData, uploadedPhotos, template, invitationContext, t]);

    // Hook pour gérer la confirmation de sortie
    const exitPath = `/public/invite/${invitationUuid}`;
    // Pas de modifications non sauvegardées si le rapport est finalisé
    const hasUnsavedChanges = !!draftData && Object.keys(formData).length > 0 && invitationContext?.status !== 'completed';
    const { isDialogOpen, setIsDialogOpen, confirmExit, requestExit, isSaving } = useExitConfirmation(
        hasUnsavedChanges,
        exitPath,
        { onBeforeExit: saveDraftBeforeExit },
    );

    // Navigation
    const handlePrevious = () => {
        if (currentSectionIndex === 0) {
            // Sur la première section, demander confirmation de sortie
            requestExit();
        } else {
            setCurrentSectionIndex((prev) => prev - 1);
        }
    };

    const handleNext = () => {
        if (currentSectionIndex < totalSections - 1) {
            setCurrentSectionIndex((prev) => prev + 1);
        }
    };

    // Capture photo / vidéo
    const handlePhotoCapture = (fieldId: string) => {
        setCaptureFieldId(fieldId);
        setShowPhotoCapture(true);
    };

    const closeCaptureSheet = () => {
        setShowPhotoCapture(false);
        setCaptureFieldId(null);
    };

    // Le champ en cours de capture détermine la sheet à ouvrir (photo ou vidéo)
    const captureField = useMemo(() => {
        if (!captureFieldId) return null;
        for (const section of sections) {
            for (const field of section.fields || []) {
                if (`${section.id}-${field.id}` === captureFieldId) return field;
            }
        }
        return null;
    }, [captureFieldId, sections]);
    const isVideoCapture = isVideoFieldType(captureField?.type);

    const handlePhotoCaptured = async (photoData: any) => {
        console.log('[InviteReportPage] handlePhotoCaptured', photoData);
        if (!captureFieldId || !invitationUuid) return;

        setShowPhotoCapture(false);
        setIsUploadingPhoto(captureFieldId);

        try {
            const isVideo = photoData?.mediaType === 'video';

            const response = await horodatyApi.uploadGuestPicture(invitationUuid, {
                file_name:
                    photoData.fileName ||
                    photoData.file_name ||
                    (isVideo ? 'video.webm' : 'photo.jpg'),
                latitude: position?.latitude ?? photoData.latitude,
                longitude: position?.longitude ?? photoData.longitude,
                // L'API attend l'identifiant du champ seul (comparé en entier), pas la clé `section-champ`
                field_id: captureField?.id,
                ...(isVideo
                    ? {
                          video: photoData.blob,
                          mime_type: photoData.mimeType,
                          duration_ms: photoData.durationMs,
                      }
                    : { base64: photoData.base64 }),
            });

            const uploadedMedia = response.data?.data || response.data;
            const uploadedPhoto = {
                ...documentToMedia({ ...uploadedMedia, mime: uploadedMedia?.mime || photoData.mimeType }, invitationUuid),
                mediaType: isVideo ? 'video' : 'photo',
            };

            // Ajouter au state
            setUploadedPhotos((prev) => ({
                ...prev,
                [captureFieldId]: [...(prev[captureFieldId] || []), uploadedPhoto],
            }));

            // Mettre à jour le formData avec les IDs des médias
            const mediaIds = [...(uploadedPhotos[captureFieldId] || []), uploadedPhoto].map((p) => p.uniq_id);
            const [sectionId, fieldId] = captureFieldId.split('-');
            handleFieldChange(sectionId, fieldId, mediaIds);

            toast.success(isVideo ? t('reportForm.videoAdded') : t('reportForm.photoAdded'));
        } catch (err: any) {
            console.error('Error uploading media:', err);
            toast.error(err.response?.data?.message || t('reportForm.mediaUploadFailed'));
        } finally {
            setIsUploadingPhoto(null);
            setCaptureFieldId(null);
        }
    };

    // Finalisation du rapport
    const handleSubmit = async () => {
        console.log('[Report] handleSubmit called - invitationUuid:', invitationUuid, 'template:', !!template?.configuration?.sections);
        if (!invitationUuid || !template?.configuration?.sections) {
            console.log('[Report] handleSubmit ABORTED - missing data');
            return;
        }

        // Valider les champs requis manuellement avec les bonnes clés
        const missingFields: string[] = [];
        template.configuration.sections.forEach((section: any) => {
            (section.fields || []).forEach((field: any) => {
                if (field.required) {
                    const fieldKey = `${section.id}-${field.id}`;

                    if (isMediaFieldType(field.type)) {
                        const photos = uploadedPhotos[fieldKey] || [];
                        const minPhotos = getMinMediaCount(field);
                        if (photos.length < minPhotos) {
                            missingFields.push(field.label);
                        }
                    } else {
                        const value = formData[fieldKey];
                        const isEmpty = value === null || value === undefined || value === '' ||
                            (Array.isArray(value) && value.length === 0);
                        if (isEmpty) {
                            missingFields.push(field.label);
                        }
                    }
                }
            });
        });

        if (missingFields.length > 0) {
            toast.error(t('reportForm.missingRequiredFields', { fields: missingFields.join(', ') }));
            return;
        }

        setIsFinalizing(true);

        try {
            // Construire les answers manuellement avec les bonnes clés (sectionId-fieldId)
            const answers = {
                sections: template.configuration.sections.map((section: any) => ({
                    id: section.id,
                    fields: (section.fields || []).map((field: any) => {
                        const fieldKey = `${section.id}-${field.id}`;

                        // Pour les champs média (photo / vidéo), utiliser les IDs des documents uploadés
                        if (isMediaFieldType(field.type)) {
                            const photos = uploadedPhotos[fieldKey] || [];
                            return {
                                id: field.id,
                                type: frontendToBackendFieldType(field.type),
                                value: photos.map((p: any) => p.uniq_id),
                            };
                        }
                        
                        // Pour les autres champs, utiliser formData
                        return {
                            id: field.id,
                            type: frontendToBackendFieldType(field.type),
                            value: formData[fieldKey] ?? null,
                        };
                    }),
                })),
            };

            console.log('[Report] handleSubmit - answers built:', JSON.stringify(answers).substring(0, 500));
            console.log('[Report] handleSubmit - calling finalizeGuestReport, invitationUuid:', invitationUuid);

            await horodatyApi.finalizeGuestReport(invitationUuid, { answers });

            // Marquer l'invitation comme complétée en mémoire
            console.log('[Report] Finalization SUCCESS, calling markInvitationCompleted');
            markInvitationCompleted();

            // Rediriger vers la page de succès
            console.log('[Report] Navigating to success page');
            navigate(`/public/invite/${invitationUuid}/success`);
        } catch (err: any) {
            console.error('Error finalizing report:', err);
            toast.error(err.response?.data?.message || t('reportForm.submissionFailed'));
        } finally {
            setIsFinalizing(false);
        }
    };

    // État de chargement
    if (isLoadingTemplate) {
        return (
            <div className="bg-surface min-h-[calc(100vh-7rem)] flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-secondary" />
                    <p className="text-[14px] text-muted-foreground">{t('reportForm.loadingReport')}</p>
                </div>
            </div>
        );
    }

    // Rapport finalisé — afficher la vue read-only
    const isCompleted = invitationContext?.status === 'completed'
        || interventionData?.procedureReport?.status === 'completed';

    if (isCompleted && interventionData) {
        return (
            <GuestReportReadOnly
                intervention={interventionData}
                invitationContext={invitationContext}
                invitationUuid={invitationUuid!}
            />
        );
    }

    // Erreur
    if (error || !template) {
        return (
            <div className="bg-surface min-h-[calc(100vh-7rem)] flex items-center justify-center px-[20px] pb-16">
                <div className="max-w-md w-full bg-card rounded-[16px] shadow-lg p-[32px] text-center">
                    <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-destructive" />
                    </div>
                    <h1 className="text-[20px] font-semibold mb-2">{t('reportForm.errorTitle')}</h1>
                    <p className="text-[14px] text-muted-foreground mb-6">
                        {error || t('reportForm.loadFailed')}
                    </p>
                    <Button
                        onClick={() => navigate(`/public/invite/${invitationUuid}`)}
                        className="h-auto rounded-[12px] py-[14px] px-[24px] text-[14px] bg-brand text-foreground-dark hover:bg-brand/90"
                    >
                        {t('reportForm.backButton')}
                    </Button>
                </div>
            </div>
        );
    }

    // Couleur du template
    const templateColor = template?.configuration?.color || template?.color || '#34DCA7';

    return (
        <div className="bg-surface relative size-full overflow-hidden flex flex-col">
            {/* Exit Confirmation Dialog */}
            <AlertDialogExitConfirmation
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                onConfirm={confirmExit}
                isSaving={isSaving}
                saveOnExit={hasUnsavedChanges}
            />
            {/* Header */}
            <div className="bg-card shadow-sm">
                <div className="px-[20px] py-[16px] flex items-center gap-[12px]">
                    <button
                        type="button"
                        onClick={handlePrevious}
                        className="p-2 -ml-2 text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="flex-1">
                        <h1 className="text-[18px] text-foreground">{template.name}</h1>
                        <p className="text-[12px] text-muted-foreground">
                            {t('reportForm.sectionProgress', {
                                current: currentSectionIndex + 1,
                                total: totalSections,
                            })}
                        </p>
                    </div>
                </div>
                {/* Progress Bar */}
                <div className="h-[4px] bg-surface">
                    <div
                        className="h-full transition-all duration-300"
                        style={{ width: `${progress}%`, backgroundColor: templateColor }}
                    />
                </div>
            </div>

            {/* Content */}
            <div
                className="flex-1 overflow-y-auto scrollbar-hide pb-[120px]"
                style={{
                    background: `linear-gradient(to right bottom, ${templateColor}15, ${templateColor}08)`,
                }}
            >
                <div className="px-[20px] pt-[20px]">
                    {currentSection && (
                        <div>
                            {/* Titre de la section */}
                            <div className="mb-[20px]">
                                <h2 className="text-[16px] text-foreground font-semibold">{currentSection.name}</h2>
                                {currentSection.description && (
                                    <p className="text-[13px] text-muted-foreground mt-[4px]">
                                        {currentSection.description}
                                    </p>
                                )}
                            </div>

                            {/* Champs */}
                            <div className="space-y-[20px]">
                                {currentSection.fields?.map((field: any) => {
                                    const fieldKey = `${currentSection.id}-${field.id}`;
                                    return (
                                        <FieldRenderer
                                            key={fieldKey}
                                            field={field}
                                            value={formData[fieldKey]}
                                            onChange={(value) => handleFieldChange(currentSection.id, field.id, value)}
                                            onPhotoCapture={() => handlePhotoCapture(fieldKey)}
                                            onVideoClick={setPlayingVideo}
                                            uploadedPhotos={uploadedPhotos[fieldKey]}
                                            isUploadingPhoto={isUploadingPhoto === fieldKey}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 bg-card border-t border-border px-[20px] py-[16px]">
                <div className="flex gap-[12px]">
                    {currentSectionIndex > 0 && (
                        <Button
                            variant="plain"
                            onClick={handlePrevious}
                            className="flex-1 h-auto rounded-[12px] py-[16px] text-[16px] transition-colors bg-surface text-foreground hover:bg-surface-hover"
                        >
                            {t('reportForm.previousButton')}
                        </Button>
                    )}
                    <Button
                        onClick={isLast ? handleSubmit : handleNext}
                        disabled={isFinalizing}
                        className={`flex-1 h-auto rounded-[12px] py-[16px] text-[16px] transition-colors flex items-center justify-center gap-[8px] ${
                            isFinalizing
                                ? 'bg-surface text-muted-foreground cursor-not-allowed'
                                : 'bg-brand text-foreground-dark hover:bg-brand/90'
                        }`}
                    >
                        {isFinalizing ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t('reportForm.finalizingEllipsis')}
                            </>
                        ) : isLast ? (
                            t('reportForm.finishAndValidateButton')
                        ) : (
                            <>
                                {t('reportForm.nextButton')}
                                <ChevronRight className="h-5 w-5" />
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Lecture d'une vidéo du rapport */}
            <GuestVideoDialog
                video={playingVideo}
                invitationUuid={invitationUuid!}
                onClose={() => setPlayingVideo(null)}
            />

            {/* Sheet de capture : dédiée vidéo ou photo selon le champ actif */}
            {isVideoCapture ? (
                <VideoCaptureSheet
                    open={showPhotoCapture}
                    onClose={closeCaptureSheet}
                    onCapture={handlePhotoCaptured}
                    isUploading={!!isUploadingPhoto}
                />
            ) : (
                <SheetPhotoCapture
                    open={showPhotoCapture}
                    onClose={closeCaptureSheet}
                    onCapture={handlePhotoCaptured}
                    isUploading={!!isUploadingPhoto}
                />
            )}
        </div>
    );
}
